import React, { useEffect, useState } from "react";
import {
  Typography,
  Paper,
  Box,
  Divider,
  CircularProgress,
  Button,
  LinearProgress,
  Alert, // Added for status display
} from "@mui/material";
import { useSearchParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { PDFDocument, rgb } from "pdf-lib";

function Receipt() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const vNum = searchParams.get("vNum") || "UNKNOWN";
  const [receiptData, setReceiptData] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [progress, setProgress] = useState(0);
  const [downloadStatus, setDownloadStatus] = useState(""); // New state for download status

  const locationMap = {
    allzones: "All Zones",
    zone1: "Damber Chowk Zone",
    zone2: "9th Mile Zone",
    zone3: "10th Mile Zone",
  };

  useEffect(() => {
    if (vNum === "UNKNOWN") {
      navigate("/");
      return;
    }

    const fetchData = async () => {
      try {
        const docRef = doc(db, "vehiclemaster", vNum);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setReceiptData(docSnap.data());
        } else {
          alert("Receipt not found!");
          navigate("/");
        }
      } catch (error) {
        console.error("Error fetching receipt data:", error);
        navigate("/");
      }
    };

    fetchData();
  }, [vNum, navigate]);

  useEffect(() => {
    if (!receiptData?.end || !receiptData?.start) return;

    const calculateTime = () => {
      const now = new Date();
      const startDate = receiptData.start.toDate ? receiptData.start.toDate() : receiptData.start;
      const endDate = receiptData.end.toDate ? receiptData.end.toDate() : receiptData.end;
      const totalDuration = endDate - startDate;
      const elapsed = now - startDate;

      // Calculate progress percentage
      const progressPercent = Math.min((elapsed / totalDuration) * 100, 100);
      setProgress(progressPercent);

      // Calculate time left
      const diffMs = endDate - now;
      if (diffMs <= 0) {
        setTimeLeft(null);
        return;
      }

      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (days >= 1) {
        setTimeLeft({ type: "days", value: days });
        return;
      }

      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
      setTimeLeft({ type: "time", hours, minutes, seconds });
    };

    calculateTime();
    const timer = setInterval(calculateTime, 1000);

    return () => clearInterval(timer);
  }, [receiptData]);

  const formatDateTime = (ts) => {
    if (!ts) return "N/A";
    const date = ts.toDate ? ts.toDate() : ts;
    const dateStr = date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const timeStr = date.toLocaleTimeString("en-IN", {
      hour12: false,
    });
    return `${dateStr} at ${timeStr}`;
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return "N/A";
    const startDate = start.toDate ? start.toDate() : start;
    const endDate = end.toDate ? end.toDate() : end;
    const diffMs = endDate - startDate;
    if (diffMs <= 0) return "N/A";

    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) return `${days} day${days > 1 ? "s" : ""}`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""}`;
    return `${minutes} minute${minutes > 1 ? "s" : ""}`;
  };

  const handleDownloadPDF = async () => {
    if (!receiptData) {
      setDownloadStatus("Receipt data is not available yet.");
      setTimeout(() => setDownloadStatus(""), 3000);
      return;
    }

    try {
      setDownloadStatus("Fetching document...");

      // Fetch the base PDF
      const basePdfUrl = "/base.pdf";
      const basePdfResponse = await fetch(basePdfUrl);
      if (!basePdfResponse.ok) {
        throw new Error("Failed to load base PDF");
      }
      const basePdfBytes = await basePdfResponse.arrayBuffer();

      setDownloadStatus("Generating document...");

      // Load the base PDF using pdf-lib
      const basePdfDoc = await PDFDocument.load(basePdfBytes);
      const pdfDoc = await PDFDocument.create();
      const [basePage] = await pdfDoc.copyPages(basePdfDoc, [0]); // Copy the first page of base PDF
      pdfDoc.addPage(basePage);

      // Get the first page to overlay content
      const page = pdfDoc.getPage(0);
      const { width, height } = page.getSize();

      // Define fields for PDF with coordinates (in points, 1 mm = 2.83465 points)
      const fields = [
        {
          label: "",
          value: "KPG/25-26/" + receiptData.recentid.substring(4) || "N/A",
          x: 60 * 2.83465,
          y: height - (77 * 2.83465),
        },
        {
          label: "",
          value: receiptData.vno || "N/A",
          x: 80 * 2.83465,
          y: height - (110 * 2.83465),
        },
        {
          label: "",
          value: formatDateTime(receiptData.start),
          x: 80 * 2.83465,
          y: height - (122 * 2.83465),
        },
        {
          label: "",
          value: formatDateTime(receiptData.end),
          x: 80 * 2.83465,
          y: height - (134 * 2.83465),
        },
        {
          label: "",
          value: calculateDuration(receiptData.start, receiptData.end),
          x: 80 * 2.83465,
          y: height - (145 * 2.83465),
        },
        {
          label: "",
          value: locationMap[receiptData.location?.toLowerCase()] || receiptData.location || "N/A",
          x: 80 * 2.83465,
          y: height - (157 * 2.83465),
        },
        {
          label: "",
          value: receiptData.amountPaid ? `Rs.${receiptData.amountPaid}` : "N/A",
          x: 80 * 2.83465,
          y: height - (168 * 2.83465),
        },
      ];

      // Add fields to the page
      page.setFontSize(16);
      fields.forEach((field) => {
        page.drawText(`${field.label} ${field.value}`, {
          x: field.x,
          y: field.y,
          size: 16,
          color: rgb(0, 0, 0),
        });
      });

      // Add "Receipt generated on" text at bottom-right
      const generatedDate = new Date().toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "medium",
      });
      page.setFontSize(10);
      page.drawText(`Receipt generated on: ${generatedDate}`, {
        x: 120 * 2.83465,
        y: 3 * 2.83465,
        size: 10,
        color: rgb(0, 0, 0),
        align: "right",
      });

      setDownloadStatus("Downloading document...");

      // Save the PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `parking_receipt_${vNum}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);

      setDownloadStatus("Document downloaded!");
      setTimeout(() => setDownloadStatus(""), 3000); // Clear status after 3 seconds
    } catch (error) {
      console.error("Error generating PDF:", error);
      setDownloadStatus("Failed to generate PDF. Please try again.");
      setTimeout(() => setDownloadStatus(""), 3000); // Clear error after 3 seconds
    }
  };

  if (!receiptData) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const isExpired = !timeLeft;

  const readableLocation = receiptData.location
    ? locationMap[receiptData.location.toLowerCase()] || receiptData.location
    : "N/A";

  const handleRenew = () => {
    navigate(`/pay?vNum=${vNum}&ref=renew`);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#f4f9ff",
        minHeight: "100vh",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Status Message */}
      {downloadStatus && (
        <Alert
          severity={downloadStatus.includes("Failed") ? "error" : "info"}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            borderRadius: 0,
            justifyContent: "center",
          }}
        >
          {downloadStatus}
        </Alert>
      )}

      <div className="header">
        <img src="/logo.png" alt="Logo" className="logo" />
        <div className="heading-text">
          <br />
          <h1 className="title">Smart Parking App</h1>
          <p className="caption">"Empowering Digital India .."</p>
        </div>
      </div>
      <h1 className="title">Parking Receipt</h1>

      <Paper
        elevation={3}
        sx={{
          p: 3,
          borderRadius: 4,
          position: "relative",
          backgroundColor: "#ffffff",
          maxWidth: 380,
          width: "87%",
          mx: "auto",
        }}
      >
        {isExpired && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              backgroundColor: "#ff0000",
              color: "#fff",
              px: 2,
              py: 0.5,
              fontWeight: "bold",
              fontSize: "0.8rem",
              opacity: 0.6,
              transform: "rotate(-10deg)",
              borderRadius: 1,
            }}
          >
            EXPIRED
          </Box>
        )}
        <br />

        <Typography variant="h6" gutterBottom>
          Vehicle Number: <strong>{receiptData.vno || "N/A"}</strong>
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <br />
          <div style={{ whiteSpace: "nowrap" }}>
            <Typography>
              <strong>Location:</strong> {readableLocation}
            </Typography>
          </div>
          <Typography>
            <strong>Payment Type:</strong>{" "}
            {receiptData.pmode ? String(receiptData.pmode).charAt(0).toUpperCase() + String(receiptData.pmode).slice(1) : "N/A"}
          </Typography>
          <Typography>
            <div style={{ whiteSpace: "nowrap" }}>
              <strong>Start Date:</strong> {formatDateTime(receiptData.start)}
            </div>
          </Typography>
          <Typography>
            <div style={{ whiteSpace: "nowrap" }}>
              <strong>End Date:</strong> {formatDateTime(receiptData.end)}
            </div>
          </Typography>
          <Typography>
            <strong>Amount Paid:</strong> {receiptData.amountPaid ? `₹${receiptData.amountPaid}` : "N/A"}
          </Typography>
        </Box>

        {isExpired && (
          <Box mt={3} textAlign="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleRenew}
            >
              Renew Parking
            </Button>
          </Box>
        )}
      </Paper>

      <Box mt={3} textAlign="center">
        <Button
          variant="contained"
          color="secondary"
          onClick={handleDownloadPDF}
        >
          Download Receipt as PDF
        </Button>
      </Box>

      {timeLeft && (
        <Box sx={{ textAlign: "center", mt: 2 }}>
          <Typography variant="h6" color="textSecondary">
            Expires in:{" "}
            {timeLeft.type === "days"
              ? `${timeLeft.value} day(s)`
              : `${timeLeft.hours}h ${timeLeft.minutes}m ${timeLeft.seconds}s`}
          </Typography>
          <Box sx={{ mt: 2, maxWidth: 300, mx: "auto" }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: "#4caf50", // Green base color
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#ff0000", // Red fill color
                  transform: "scaleX(-1)", // Flip horizontally
                },
                transform: "rotate(180deg)", // Flip vertically
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default Receipt;